import React from 'react'

// Local drop-in replacement for @mui/material/Typography. Renders plain
// semantic HTML with Tailwind classes instead of pulling MUI's Typography
// into shared chunks. Covers the subset of props actually used across the
// codebase — variant, component, gutterBottom, noWrap, align/textAlign,
// color, font sizing, and MUI-style spacing shorthands (m/mx/my/mt/mb/...).
//
// The MUI spacing unit is 8px (1 unit = 8px). Shorthand props (m, mt, mb, …)
// accept a number that's multiplied by 8 and emitted as inline style.
//
// For advanced `sx` usage, convert the call site to semantic HTML + Tailwind
// manually — this wrapper intentionally does NOT reimplement the sx runtime.

type Variant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'subtitle1'
  | 'subtitle2'
  | 'body1'
  | 'body2'
  | 'caption'
  | 'button'
  | 'overline'
  | 'inherit'

const VARIANT_CLASS: Record<Variant, string> = {
  h1: 'text-[6rem] font-light leading-[1.167] tracking-[-0.01562em]',
  h2: 'text-[3.75rem] font-light leading-[1.2] tracking-[-0.00833em]',
  h3: 'text-[3rem] font-normal leading-[1.167]',
  h4: 'text-[2.125rem] font-normal leading-[1.235] tracking-[0.00735em]',
  h5: 'text-2xl font-normal leading-[1.334]',
  h6: 'text-xl font-medium leading-[1.6] tracking-[0.0075em]',
  subtitle1: 'text-base font-normal leading-[1.75] tracking-[0.00938em]',
  subtitle2: 'text-sm font-medium leading-[1.57] tracking-[0.00714em]',
  body1: 'text-base font-normal leading-[1.5] tracking-[0.00938em]',
  body2: 'text-sm font-normal leading-[1.43] tracking-[0.01071em]',
  caption: 'text-xs font-normal leading-[1.66] tracking-[0.03333em]',
  button: 'text-sm font-medium leading-[1.75] uppercase tracking-[0.02857em]',
  overline: 'text-xs font-normal leading-[2.66] uppercase tracking-[0.08333em]',
  inherit: ''
}

const DEFAULT_ELEMENT: Record<Variant, keyof React.JSX.IntrinsicElements> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  subtitle1: 'h6',
  subtitle2: 'h6',
  body1: 'p',
  body2: 'p',
  caption: 'span',
  button: 'span',
  overline: 'span',
  inherit: 'p'
}

type Align =
  | 'left'
  | 'center'
  | 'right'
  | 'justify'
  | 'start'
  | 'end'
  | 'inherit'

// MUI props optionally accept `{ xs: ..., sm: ..., md: ..., lg: ..., xl: ... }`
// for breakpoint-responsive values. We can't do real breakpoint-conditional
// styling without a CSS-in-JS runtime, so we pick the largest defined value
// (mobile-first cascade would use `xs`; desktop-first falls back to `md`).
// For the handful of call sites passing responsive values to Typography, the
// largest-breakpoint value is the right approximation — they're mostly
// typography-sizing overrides that grow on desktop.
type Responsive<T> =
  | T
  | {
      xs?: T
      sm?: T
      md?: T
      lg?: T
      xl?: T
    }

const resolveResponsive = <T,>(
  value: Responsive<T> | undefined
): T | undefined => {
  if (value === undefined || value === null) return undefined
  if (typeof value !== 'object') return value as T
  const v = value as Record<string, T>
  return v.xl ?? v.lg ?? v.md ?? v.sm ?? v.xs
}

// Map MUI named theme colors to sensible Tailwind palette equivalents. Any
// other color string is passed through as a CSS color (inline style).
const NAMED_COLORS: Record<string, string> = {
  'text.primary': 'inherit',
  'text.secondary': '#607d8b',
  'text.disabled': '#9e9e9e',
  primary: '#1976d2',
  secondary: '#9c27b0',
  error: '#d32f2f',
  warning: '#ed6c02',
  info: '#0288d1',
  success: '#2e7d32',
  red: '#d32f2f'
}

const toPx = (v: number | string | undefined): string | undefined => {
  if (v === undefined) return undefined
  if (typeof v === 'number') return `${v * 8}px`
  return v
}

type AnyTag = keyof React.JSX.IntrinsicElements

type Spacing = Responsive<number | string>

type TypographyProps = React.HTMLAttributes<HTMLElement> & {
  variant?: Variant
  component?: AnyTag
  align?: Responsive<Align>
  textAlign?: Responsive<Align>
  gutterBottom?: boolean
  noWrap?: boolean
  paragraph?: boolean
  color?: string
  fontSize?: Responsive<number | string>
  fontWeight?: Responsive<number | string>
  lineHeight?: Responsive<number | string>
  letterSpacing?: Responsive<number | string>
  // MUI-style spacing shorthand (1 unit = 8px). Also accepts `{ xs, md, ... }`
  // responsive objects — we pick the largest breakpoint defined.
  m?: Spacing
  mt?: Spacing
  mb?: Spacing
  ml?: Spacing
  mr?: Spacing
  mx?: Spacing
  my?: Spacing
  p?: Spacing
  pt?: Spacing
  pb?: Spacing
  pl?: Spacing
  pr?: Spacing
  px?: Spacing
  py?: Spacing
  marginTop?: Spacing
  marginBottom?: Spacing
  marginLeft?: Spacing
  marginRight?: Spacing
  paddingTop?: Spacing
  paddingBottom?: Spacing
  paddingLeft?: Spacing
  paddingRight?: Spacing
  // Accept but ignore: sx (intentionally unsupported — migrate call sites)
  sx?: Record<string, unknown>
  children?: React.ReactNode
  className?: string
}

export default function Typography({
  variant = 'body1',
  component,
  align,
  textAlign,
  gutterBottom,
  noWrap,
  paragraph,
  color,
  fontSize,
  fontWeight,
  lineHeight,
  letterSpacing,
  m,
  mt,
  mb,
  ml,
  mr,
  mx,
  my,
  p,
  pt,
  pb,
  pl,
  pr,
  px,
  py,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  paddingTop,
  paddingBottom,
  paddingLeft,
  paddingRight,
  className = '',
  style,
  children,
  ...rest
}: TypographyProps) {
  const Tag = (component ?? DEFAULT_ELEMENT[variant]) as AnyTag
  const r = resolveResponsive
  const alignment = r(textAlign) ?? r(align)
  const resolvedColor = color ? (NAMED_COLORS[color] ?? color) : undefined
  const fs = r(fontSize)
  const fw = r(fontWeight)
  const lh = r(lineHeight)
  const ls = r(letterSpacing)
  const _m = r(m)
  const _mx = r(mx)
  const _my = r(my)
  const _mt = r(mt) ?? r(marginTop)
  const _mb = r(mb) ?? r(marginBottom)
  const _ml = r(ml) ?? r(marginLeft)
  const _mr = r(mr) ?? r(marginRight)
  const _p = r(p)
  const _px = r(px)
  const _py = r(py)
  const _pt = r(pt) ?? r(paddingTop)
  const _pb = r(pb) ?? r(paddingBottom)
  const _pl = r(pl) ?? r(paddingLeft)
  const _pr = r(pr) ?? r(paddingRight)

  const inline: React.CSSProperties = {
    ...(resolvedColor ? { color: resolvedColor } : {}),
    ...(fs !== undefined
      ? { fontSize: typeof fs === 'number' ? `${fs}px` : fs }
      : {}),
    ...(fw !== undefined ? { fontWeight: fw } : {}),
    ...(lh !== undefined ? { lineHeight: lh } : {}),
    ...(ls !== undefined
      ? { letterSpacing: typeof ls === 'number' ? `${ls}px` : ls }
      : {}),
    ...(alignment ? { textAlign: alignment } : {}),
    ...(toPx(_m) ? { margin: toPx(_m) } : {}),
    ...(toPx(_mx) ? { marginLeft: toPx(_mx), marginRight: toPx(_mx) } : {}),
    ...(toPx(_my) ? { marginTop: toPx(_my), marginBottom: toPx(_my) } : {}),
    ...(toPx(_mt) ? { marginTop: toPx(_mt) } : {}),
    ...(toPx(_mb) ? { marginBottom: toPx(_mb) } : {}),
    ...(toPx(_ml) ? { marginLeft: toPx(_ml) } : {}),
    ...(toPx(_mr) ? { marginRight: toPx(_mr) } : {}),
    ...(toPx(_p) ? { padding: toPx(_p) } : {}),
    ...(toPx(_px) ? { paddingLeft: toPx(_px), paddingRight: toPx(_px) } : {}),
    ...(toPx(_py) ? { paddingTop: toPx(_py), paddingBottom: toPx(_py) } : {}),
    ...(toPx(_pt) ? { paddingTop: toPx(_pt) } : {}),
    ...(toPx(_pb) ? { paddingBottom: toPx(_pb) } : {}),
    ...(toPx(_pl) ? { paddingLeft: toPx(_pl) } : {}),
    ...(toPx(_pr) ? { paddingRight: toPx(_pr) } : {}),
    ...style
  }

  const classes = [
    VARIANT_CLASS[variant],
    gutterBottom ? 'mb-[0.35em]' : '',
    paragraph ? 'mb-4' : '',
    noWrap ? 'overflow-hidden whitespace-nowrap text-ellipsis' : '',
    className
  ]
    .filter(Boolean)
    .join(' ')

  return React.createElement(
    Tag,
    { ...rest, className: classes, style: inline },
    children
  )
}
